import { useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import { setSelectedCellIds, syncSelectedCellIds } from "../../features/RouteSlice.js";
import { useDemoScenario } from "../../demo/DemoScenarioContext";
import { writeDemoCellSelection } from "../../demo/demoCellSelection";
import { useT } from "../../i18n";

/**
 * CellSelectionBar
 * ------------------------------------------------------------
 * 지도 좌하단 농장 이름 위에 붙는 선택 해제 컨트롤.
 *
 * 전체 해제는 원래 미션 탭에만 있었는데, 그 탭은 드론이 선택돼야 열린다.
 * 드론이 없는 농장에서는 고른 셀을 한 번에 지울 방법이 아예 없었다.
 * 해제를 지도 위로 올려 드론 유무와 무관하게 쓸 수 있게 한다.
 *
 * 미션 중에는 이 목록이 사용자의 선택이 아니라 진행 중 경로 표시라서 숨긴다.
 * 전체 선택도 여기서 한다(예전 Waypoint List 카드의 Select All). 그 카드는 미션 박스로 합쳐져 없어졌다.
 */
// variant: "map"(기본) 은 지도 위에 띄우고, "inline" 은 미션 탭 머리글 안에 넣는다.
// 모바일에서는 지도 위 자리가 없어 미션 탭의 빈 공간(드론 이름 오른쪽)에 둔다(CalmTheme.css ≤900px).
export default function CellSelectionBar({ variant = "map" }) {
  const dispatch = useDispatch();
  const t = useT();
  // 데모의 선택은 서버가 아니라 브라우저에 있다. syncSelectedCellIds 를 그냥 부르면
  // 실제 농장의 공유 선택을 덮어쓰고 STOMP 로 다른 화면까지 바뀐다
  // (useFarmCellSelection 은 막아 뒀는데 이 버튼만 그 경로를 안 타고 있었다).
  const { isDemo } = useDemoScenario();
  const farmId = useSelector((state) => state.farm.farmId);
  const selectedCellIds = useSelector((state) => state.route.selectedCellIds);
  const gridCells = useSelector((state) => state.farm.gridCells);
  const missionStatus = useSelector((state) => state.drone.control?.missionStatus);
  const [confirming, setConfirming] = useState(false);

  const count = selectedCellIds?.length ?? 0;
  const total = Array.isArray(gridCells) ? gridCells.length : 0;
  if (total === 0 || missionStatus) return null;

  const applySelection = (cellIds) => {
    if (isDemo) {
      dispatch(setSelectedCellIds(writeDemoCellSelection(farmId, cellIds)));
      return;
    }
    dispatch(syncSelectedCellIds(cellIds));
  };

  const selectAll = () => {
    applySelection(gridCells.map((cell) => cell.id).filter(Boolean));
  };

  const clear = () => {
    applySelection([]);
    setConfirming(false);
  };

  return (
    <>
      <div className={`calm-cellbar${variant === "inline" ? " calm-cellbar--inline" : ""}`}>
        <span className="calm-cellbar-count">
          <i aria-hidden="true" />
          {count > 0 ? (
            <>
              {/* 숫자만 굵게 보이도록 개수와 뒤 문구를 나눠 그린다. */}
              <b>{count}</b>{t("common.cellBar.selectedSuffix")}
            </>
          ) : (
            t("common.cellBar.none")
          )}
        </span>
        {count < total && (
          <button type="button" className="calm-cellbar-clear" onClick={selectAll}>
            {t("common.cellBar.selectAll")}
          </button>
        )}
        {count > 0 && (
          <button type="button" className="calm-cellbar-clear" onClick={() => setConfirming(true)}>
            {t("common.cellBar.clear")}
          </button>
        )}
      </div>

      {/* 확인창은 시트 안에 갇히지 않도록 body 로 내보낸다(scrim 은 fixed). */}
      {confirming && createPortal(
        <div className="calm-cellbar-scrim" onClick={() => setConfirming(false)}>
          <div
            className="calm-cellbar-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cellbar-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="cellbar-dialog-title">{t("common.cellBar.confirmTitle", { count })}</h2>
            {/* 선택은 농장 단위 공유 값이라 누르는 순간 다른 사람 화면에서도 사라진다. */}
            <p>{t("common.cellBar.confirmBody")}</p>
            <div className="calm-cellbar-actions">
              <button type="button" className="ghost" onClick={() => setConfirming(false)}>
                {t("common.cellBar.cancel")}
              </button>
              <button type="button" className="danger" onClick={clear}>
                {t("common.cellBar.clear")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
